import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { urlToPattern } from "../urlToPattern";
import logo from "./logo.svg";

const apiUrl = (value: string | undefined) =>
  (value ?? "").trim().replace(/\/+$/, "");

export const obsidian = defineConnector({
  identifier: "obsidian",
  name: "Obsidian",
  tagLine: "Private notes and knowledge base",
  keywords: "markdown vault local REST API",
  logo,
  protocol: "http",
  tags: ["documents", "productivity"],
  defaultUrlPattern: ({ instanceUrl }) => urlToPattern(instanceUrl),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Enable API access in Obsidian",
      items: [
        {
          kind: "markdown",
          content: `1. Install **Local REST API with MCP** 4.1.3 or later. Older versions can access files outside the vault.
2. Make the Obsidian host reachable from 3B over a private network or tunnel. Never expose the API to the public internet.
3. In **Settings → Local REST API with MCP → Advanced settings**, set **Binding host** to a private address 3B can reach. Leave **Authorization header** set to \`Authorization\`.
4. Under **Certificates → Certificate hostnames**, add the hostname from your Obsidian API URL, then regenerate the certificates.
5. Download the new \`obsidian-local-rest-api.crt\`, and copy the API key.
6. Upload the certificate as the **CA certificate** in the final “Customize this connector” step.`,
        },
      ],
    },
    {
      title: "Enter your Obsidian API details",
      items: [
        {
          kind: "url",
          label: "Obsidian API URL",
          required: true,
          placeholder: "https://obsidian.example.com:27124",
          helperText: "The base URL of the Local REST API server.",
          target: { kind: "configuration", field: "instanceUrl" },
        },
        {
          kind: "input",
          label: "API key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ instanceUrl }) => `${apiUrl(instanceUrl)}/vault/`,
  },
  publicEnvVars: ({ instanceUrl }) => ({
    OBSIDIAN_URL: apiUrl(instanceUrl),
  }),
  usageHint: () =>
    "Base URL: process.env.OBSIDIAN_URL. API docs: https://coddingtonbear.github.io/obsidian-local-rest-api/.",
});
